import { LANGUAGE_KEYS, LANGUAGE_INFO, PROVIDER_GEMINI_API } from '../../Mobx/AudioCatalog';

/**
 * The form of the audio settings screen. The fields of the form are strings, as the user typed them -
 * these functions convert the settings of the server (GET /api/ai/settings/audio) to the form,
 * decide what has to be saved, and check the form before it is sent.
 * No React here, so they are easy to test.
 */

const VOICE_FIELDS = ['female', 'male'];

/** The form shows what the automatic creation really uses (effective), not the raw saved values */
export const formFromView = (view) => {
    const { effective } = view;
    const form = { model: effective.model, prompt: effective.prompt };
    for (const key of LANGUAGE_KEYS) {
        const language = effective.languages?.[key] || {};
        form[key] = { female: language.female || '', male: language.male || '' };
    }
    return form;
};

/** A field is saved when the user changed it, or when the saved value is not valid (then it is replaced by the value of the form) */
const needsSave = (savedValue, effectiveValue, formValue) =>
    formValue !== effectiveValue || (savedValue !== undefined && savedValue !== effectiveValue);

/**
 * What to send to the server (PATCH /api/ai/settings/audio): { model, prompt, he: { female, male }, ... }
 * Only the fields that have to be saved. {} - nothing to save.
 */
export const buildPatch = (form, view) => {
    const configured = view.configured || {};
    const { effective } = view;
    const patch = {};

    if (needsSave(configured.model, effective.model, form.model)) patch.model = form.model;
    if (needsSave(configured.prompt, effective.prompt, form.prompt)) patch.prompt = form.prompt.trim();

    for (const key of LANGUAGE_KEYS) {
        const saved = configured[key] || {};
        const current = effective.languages?.[key] || {};
        const changes = {};
        for (const field of VOICE_FIELDS) {
            if (needsSave(saved[field], current[field], form[key][field])) changes[field] = form[key][field];
        }
        if (Object.keys(changes).length > 0) patch[key] = changes;
    }
    return patch;
};

/** ['model', 'he.female'] - the names of the fields in a patch, to show the user what is going to change */
export const patchPaths = (patch) => Object.entries(patch).flatMap(([key, value]) => (
    value !== null && typeof value === 'object' ? Object.keys(value).map((field) => `${key}.${field}`) : [key]
));

/**
 * The models need the Gemini API key of the server - without it neither the test nor the automatic creation works.
 * (ffmpeg is needed only to create the MP3 files; the settings screen shows its own warning when it is missing.)
 */
export const getUnavailableReason = (model, capabilities) => {
    if (!model || model.provider !== PROVIDER_GEMINI_API || !capabilities) return '';
    if (!capabilities.geminiApiKey) return 'לשרת אין מפתח Gemini API';
    return '';
};

const byteLength = (text) => new TextEncoder().encode(text).length;

/** @returns {string[]} what is wrong in the form (Hebrew). [] - the form can be saved. */
export const validateForm = (form, view) => {
    const problems = [];
    const { catalog, capabilities } = view;
    const limits = catalog.limits || {};
    const voices = new Set(catalog.voices || []);

    const model = catalog.models.find((entry) => entry.id === form.model);
    if (!model) {
        problems.push('לא נבחר מודל.');
    } else {
        const reason = getUnavailableReason(model, capabilities);
        if (reason) problems.push(`המודל ${model.label} אינו זמין: ${reason}.`);
    }

    if (!form.prompt.trim()) {
        problems.push('הפרומפט ריק.');
    } else if (limits.promptBytes && byteLength(form.prompt) > limits.promptBytes) {
        problems.push(`הפרומפט ארוך מדי (המגבלה: ${limits.promptBytes} בייט, באותיות עבריות כל אות היא 2 בייט).`);
    }

    for (const key of LANGUAGE_KEYS) {
        const label = LANGUAGE_INFO[key].label;
        const language = form[key];
        if (!voices.has(language.female)) problems.push(`${label}: לא נבחר קול נשי.`);
        if (!voices.has(language.male)) problems.push(`${label}: לא נבחר קול גברי.`);
    }
    return problems;
};

/** true - the user changed something in the form that was not saved */
export const isFormChanged = (form, view) => JSON.stringify(form) !== JSON.stringify(formFromView(view));
